/*
  custom-yotpo-ratings.js

  Fills the star rating on product cards from Yotpo, so collection pages stay in
  step with product pages.

  Background: the theme reads product.metafields.reviews.rating. Yotpo only writes
  that metafield when its "Shopify Metafields Sync" is active, which is not
  available in this store's Yotpo plan. Two of thirteen products have it. Where it
  does exist it also drifts, because Yotpo refreshes it on its own schedule.

  This reads Yotpo's public v3 storefront API, which needs no authentication and
  sends CORS headers for this storefront. Every rating element on the page is set
  from it, including those that already have a metafield, so nothing is ever stale.

  Endpoint history, because two earlier attempts disagreed with Yotpo's own widget
  and both were wrong (checked 2026-09-25 against all 13 products):

    v1 /bottom_lines        one request for the catalogue, but STALE AND INCOMPLETE.
                            Omitted Dotted Tull Skirt (1 review) and V Swimsuit (2)
                            entirely, so their cards showed nothing.
    v1 /{id}/bottomline     per product, fixed those two, but still stale: reported
                            20 for Cropped Knit Rib Tank against the widget's 21,
                            and 3 for Power Suit against v3's 4.
    v3 storefront           matches the widget on every product. This is what the
                            on-page widget itself uses.

  NO query parameters. Only the bottomline is needed, so perPage=1 was passed to
  trim the response from about 5 KB to 1.5 KB. That is a trap: on 2026-09-28
  `?perPage=1` returned totalReview: 0 for three products that plainly had reviews
  (Twist Tank PowerSuit 3, Off The Shoulder Crop Top 12, The Ray Suit 2), while the
  same URL without it returned the correct figures. `?page=1&perPage=1` also
  returns the correct figures, but it relies on the same paging code that produced
  the zeros, so it is not worth the 3 KB.

  Call it plainly. If the payload ever matters, cache harder rather than paginate.

  Markup comes from snippets/product-card-rating.liquid. Elements carry
  data-yotpo-product-id, and those with no metafield also carry
  data-rating-pending and are hidden by CSS until filled.
*/
(function () {
  'use strict';

  // Public widget token, already visible in the Yotpo loader URL on every page.
  var APP_TOKEN = 'kGxUOn4PqrDf3W4b9ungfREdKFdcTksahABRYima';
  var ENDPOINT = 'https://api-cdn.yotpo.com/v3/storefront/store/' + APP_TOKEN + '/product/';
  var CACHE_KEY = 'rayera:yotpo:v3:';
  var CACHE_MS = 10 * 60 * 1000;
  var SELECTOR = '.product-rating--root[data-yotpo-product-id]';
  var MAX_PARALLEL = 6;

  var ratings = {};
  var inflight = {};

  function readCache(id) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY + id);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Date.now() - parsed.at > CACHE_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(id, data) {
    try {
      sessionStorage.setItem(CACHE_KEY + id, JSON.stringify({ at: Date.now(), data: data }));
    } catch (e) {
      /* private mode, quota, disabled storage: caching is optional */
    }
  }

  function fetchOne(id) {
    if (inflight[id]) return inflight[id];

    var cached = readCache(id);
    if (cached) {
      ratings[id] = cached;
      return Promise.resolve(cached);
    }

    inflight[id] = fetch(ENDPOINT + id + '/reviews', { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('Yotpo storefront API returned ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var b = json && json.bottomline;
        var entry = {
          score: b ? Number(b.averageScore) || 0 : 0,
          count: b ? Number(b.totalReview) || 0 : 0
        };
        ratings[id] = entry;
        writeCache(id, entry);
        return entry;
      })
      .catch(function () {
        // Leave this card hidden. Everything else still fills.
        return null;
      });

    return inflight[id];
  }

  // Small pool so a large grid does not open 17 sockets at once.
  function fetchAll(ids) {
    var queue = ids.slice();
    var workers = [];

    function next() {
      if (!queue.length) return Promise.resolve();
      var id = queue.shift();
      return fetchOne(id).then(function () {
        applyAll();
        return next();
      });
    }

    for (var i = 0; i < Math.min(MAX_PARALLEL, queue.length); i++) {
      workers.push(next());
    }
    return Promise.all(workers);
  }

  function apply(el) {
    var id = el.getAttribute('data-yotpo-product-id');
    var entry = ratings[id];

    // No reviews in Yotpo. Anything still pending stays hidden.
    if (!entry || !entry.count) return;

    var score = entry.score;
    var integer = Math.floor(score);
    var decimal = Math.round((score - integer) * 100) / 100;

    el.style.setProperty('--rating', integer);
    el.style.setProperty('--rating-max', 5);
    el.style.setProperty('--rating-decimal', decimal);
    el.setAttribute('data-count', entry.count);
    el.setAttribute(
      'aria-label',
      score + ' out of 5 stars, ' + entry.count + (entry.count === 1 ? ' review' : ' reviews')
    );
    el.removeAttribute('data-rating-pending');
  }

  function applyAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(apply);
  }

  function idsOnPage(root) {
    var seen = {};
    var out = [];
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      var id = el.getAttribute('data-yotpo-product-id');
      if (id && !seen[id] && !ratings[id]) {
        seen[id] = true;
        out.push(id);
      }
    });
    return out;
  }

  function start() {
    if (!document.querySelector(SELECTOR)) return;
    watch();
    fetchAll(idsOnPage()).then(applyAll);
  }

  // Cards can arrive later from quick view, carousels or pagination.
  function watch() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var fresh = idsOnPage(node.matches && node.matches(SELECTOR) ? node.parentNode : node);
          if (fresh.length) fetchAll(fresh);
          if (node.matches && node.matches(SELECTOR)) apply(node);
          else applyAll(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
